import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, apiBase, ServerError, target } from './api';
import { soldPastZero } from './stock';
import { CATEGORY_ALL, shelvesFrom } from './shelves';
import { fa, toLatin, toman } from './fa';
import { totalsOf } from './money';
import type { Catalogue, CatalogueItem, CartLine, PaymentMethod, SalePayment, SaleResult, Shelf } from './types';
import { Shell, type NetState } from './Shell';
import { SaleScreen, type Layout } from './SaleScreen';
import { TablesScreen, type FloorTable, type SettleTarget } from './TablesScreen';
import { CartPanel } from './CartPanel';
import { PaymentScreen, type CardState } from './PaymentScreen';
import { ReceiptScreen } from './ReceiptScreen';
import { ParkModal, CustomerModal, DiscountModal, QuantityModal, NewArrivalModal, type ParkedSale, TicketDidNotPrintModal, ChargeAgainModal } from './Modals';
import { basketFingerprint } from './fingerprint';
import * as terminal from './terminal';
import { ZScreen } from './ZScreen';
import { ReturnsScreen } from './ReturnsScreen';
import { ToolsScreen } from './ToolsScreen';
import { ArrivalsScreen, type Arrival } from './ArrivalsScreen';
import { ArrivalSheet } from './ArrivalSheet';
import { unannouncedFrom } from './arrivals';
import { freshCard, type CardFields } from './cardlock';
import { ResolveHeldPayment } from './ResolveHeldPayment';
import { ConfirmExit, exitTill, hostedInTill } from './exit';
import { composePayments, settlePath, checkRef, settleOrderFrom, type Bill } from './settle';
import { connectLive, liveUrl } from './live';

type Screen = 'sale' | 'payment' | 'receipt' | 'z' | 'returns' | 'tools' | 'arrivals' | 'tables';
type Modal = 'park' | 'customer' | 'discount' | 'quantity' | null;

interface Session {
  userId: string;
  name: string;
  branchId: string;
  branchName: string;
  /**
   * The shop's own name, as the panel knows it.
   *
   * Optional because a device that has not yet replicated its merchant
   * identity has none, and a till must open and sell before it has been told
   * who it belongs to. Empty reads as unknown, never as a blank heading.
   */
  tenantName?: string;
  economicCode: string | null;
  permissions: string[];
  shiftId: string | null;
  shiftSince: string | null;
  /** Printed on receipts, and the fallback basket ref before anyone signs in. */
  deviceCode?: string;
}

const METHOD_LABEL: Record<string, string> = {
  cash: 'پرداخت نقدی',
  card: 'پرداخت با کارت‌خوان',
  transfer: 'کارت به کارت',
  credit: 'نسیه',
  voucher: 'کارت هدیه',
  online: 'پرداخت آنلاین',
  split: 'پرداخت ترکیبی (نقد + کارت)',
};

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? `${parts[0][0]}‌${parts[1][0]}` : name.slice(0, 2);
}

/*
 * A settled table's rounds, in the shape the receipt screen reads.
 *
 * The receipt renders cart lines, and a table has none — its rounds were priced
 * when they were fired. Only the fields the receipt actually shows are filled;
 * the rest of CatalogueItem is inert here and deliberately not invented, since
 * a plausible-looking variantId on a line that never came from the catalogue is
 * worse than an empty one.
 */
function settleLinesAsCart(target: SettleTarget): CartLine[] {
  return target.lines.map((l) => ({
    item: {
      variantId: '', productId: '', name: l.name, productName: l.name,
      variantLabel: null,
      priceRial: l.quantity > 0 ? Math.round(l.lineTotalRial / l.quantity) : l.lineTotalRial,
      vatBasisPoints: 0, barcode: null, sku: null, category: null,
      onHand: 0, tracksStock: false,
    },
    quantity: l.quantity,
    discountRial: 0,
  }));
}

export function Till({ session, onShiftClosed }: { session: Session; onShiftClosed: () => void }) {
  const qc = useQueryClient();

  const [screen, setScreen] = useState<Screen>('sale');
  const [arrivals, setArrivals] = useState<Arrival[]>([]);
  const [staleArrivals, setStaleArrivals] = useState(0);
  /*
   * Which arrivals have already been announced.
   *
   * Null until the first poll lands, then seeded with the ones a person has
   * already dealt with — and ONLY those. An untaken order is news whether it
   * landed a second ago or before this process started.
   *
   * It used to swallow the whole first response, on the reasoning that what is
   * already waiting when a cashier signs in is not news. That reasoning is
   * wrong in the case that matters: the till restarts — an update, a power cut,
   * a crash — and every order still waiting for «ارسال به آشپزخانه» goes silent
   * for good. It happened, with a customer at the counter and three orders
   * sitting unannounced behind a badge reading ۳. The fear it was guarding
   * against does not apply either: the modal takes the whole batch and renders
   * one list, so five waiting orders are one modal with five rows, not five
   * modals.
   *
   * Only ever added to — a dropped poll or a reconnect must not re-announce an
   * order somebody has already seen.
   */
  const announced = useRef<Set<string> | null>(null);
  const [unannounced, setUnannounced] = useState<Arrival[]>([]);
  /*
   * Counted here rather than inside the tables screen, because the number has
   * to show on the menu of a screen that is not the tables screen — an unpaid
   * table behind a menu with no sign in it is how a shop closes with a bill
   * still open.
   */
  const [openTables, setOpenTables] = useState(0);
  const [takingId, setTakingId] = useState<string | null>(null);
  // The order a cashier has opened, and what went wrong acting on it.
  const [openArrival, setOpenArrival] = useState<Arrival | null>(null);
  const [arrivalError, setArrivalError] = useState<string | null>(null);

  /*
   * Polled rather than pushed, and polled even while another screen is open.
   *
   * The count in the menu is the only thing that tells a cashier an order came
   * in, so it has to keep counting while they are mid-sale — which is exactly
   * when a kiosk order arrives. Ten seconds is slower than a person notices and
   * far cheaper than a socket the agent does not have.
   */
  useEffect(() => {
    let alive = true;
    const read = () => {
      api.get<{ arrivals: Arrival[]; staleUnclaimed?: number }>('/till/arrivals')
        .then((r) => {
          if (!alive) return;
          const list = r.arrivals ?? [];
          setArrivals(list);
          /*
           * Orders the agent deliberately left off the list: unclaimed, and
           * older than this shift. Carried through rather than dropped so the
           * bound cannot quietly lose one — the count is shown once, away from
           * the queue a cashier works from.
           */
          setStaleArrivals(r.staleUnclaimed ?? 0);

          const { seen, fresh } = unannouncedFrom(list, announced.current);
          announced.current = seen;
          if (fresh.length > 0) setUnannounced((q) => [...q, ...fresh]);
        })
        .catch(() => { /* the till keeps selling; the list catches up */ });
      /*
       * Open tables, on the same tick and for the same reason: the menu is the
       * only place a cashier learns a table is waiting to be settled, and it
       * has to keep counting while they are mid-sale.
       *
       * 404 where the branch has no floor — a grocery has no tables, and that
       * is a shape of shop, not a failure.
       */
      api.get<{ tables: FloorTable[] }>('/floor/tables')
        .then((r) => {
          if (alive) setOpenTables((r.tables ?? []).filter((t) => t.occupied && t.check).length);
        })
        .catch(() => { if (alive) setOpenTables(0); });
    };
    read();

    /*
     * The agent now says when something changed, so this is a FALLBACK rather
     * than the mechanism.
     *
     * Thirty seconds, not ten: with the stream carrying the news in about a
     * second, a ten-second poll is mostly asking a question whose answer
     * already arrived. It stays because a stream that drops and never comes
     * back must not freeze the counter -- and the shape of that failure is a
     * till that looks calm while orders pile up, which nobody notices until a
     * customer asks.
     */
    const t = setInterval(read, 30_000);

    /*
     * The live nudge. `read` re-reads arrivals and open tables; the catalogue
     * is invalidated alongside it so a stock movement on another device shows
     * here without waiting for its own refetch.
     *
     * The stream never carries the order itself -- it only says "look again" --
     * so these fetches stay the single source of truth for what is on screen.
     */
    const stopLive = connectLive(liveUrl(apiBase()), () => {
      if (!alive) return;
      read();
      void qc.invalidateQueries({ queryKey: ['till-catalogue'] });
    });

    return () => { alive = false; clearInterval(t); stopLive(); };
  }, [qc]);

  /*
   * Reprinting from an opened order.
   *
   * Separate from retryTicketPrint, which swallows the error because the
   * popup's own refetch tells the truth either way. Here a cashier has
   * deliberately opened an order and pressed a button; silence would read as
   * success, and they would walk away believing the ticket is printing.
   */
  /*
   * Taking payment for an order opened from «سفارش‌های ورودی».
   *
   * The bill is fetched rather than taken from the arrival. An arrival is ONE
   * round; a table that ordered twice owes more than the round the cashier
   * tapped, and the figure would look perfectly reasonable on screen. The
   * agent recomputes and refuses a short settle, so the cost would be a
   * refused payment rather than a lost one — but the till should not propose
   * the wrong number.
   */
  const payArrival = useCallback(async (checkId: string) => {
    setTakingId(checkId);
    setArrivalError(null);
    try {
      const bill = await api.get<Bill>(`/floor/checks/${encodeURIComponent(checkId)}`);
      setOpenArrival(null);
      payTable(settleOrderFrom(bill));
    } catch (e) {
      setArrivalError(
        e instanceof ServerError ? (e.detail ?? e.message) : 'صورت‌حساب این میز خوانده نشد',
      );
    } finally {
      setTakingId(null);
    }
    // payTable reads the current basket state, so it must not be memoised away.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reprintTicket = useCallback(async (ticketId: string) => {
    setTakingId(ticketId);
    setArrivalError(null);
    try {
      /*
       * again=1, and it is the whole point of this button.
       *
       * The same endpoint serves «تلاش دوباره», which must NOT put out a
       * second slip when a cashier presses it because the first one never
       * appeared. The till therefore ignores a ticket that already printed —
       * which is every ticket anyone would press THIS button for, so «چاپ
       * دوباره رسید آشپزخانه» reported success and produced no paper.
       *
       * The two intents are indistinguishable at the till, so the caller says
       * which it means. Omitted is the careful one.
       */
      await api.post(`/kitchen/tickets/${encodeURIComponent(ticketId)}/print?again=1`, {});
      setOpenArrival(null);
      setToast('رسید آشپزخانه دوباره چاپ شد');
    } catch (e) {
      setArrivalError(
        e instanceof ServerError ? (e.detail ?? e.message) : 'چاپ دوباره ممکن نشد',
      );
    } finally {
      setTakingId(null);
    }
  }, []);

  const fireArrival = useCallback(async (id: string) => {
    setTakingId(id);
    try {
      await api.post(`/till/arrivals/${encodeURIComponent(id)}/fire`, {});
      setArrivals((list) => list.map((a) => (a.id === id ? { ...a, takenBy: '—' } : a)));
    } catch {
      /* left in the list: an order that still looks unsent gets sent twice at
         worst, and one that vanished gets made never. */
    } finally {
      setTakingId(null);
    }
  }, []);

  const takeArrival = useCallback(async (id: string) => {
    setTakingId(id);
    try {
      await api.post(`/till/arrivals/${encodeURIComponent(id)}/seen`, {});
      // Dropped locally rather than waiting for the next poll: a cashier who
      // tapped it needs it gone now, and the poll will agree in a moment.
      // Marked taken rather than dropped: it stays on screen, quietly, so an
      // order claimed and then abandoned is still visible to whoever is there.
      setArrivals((list) => list.map((a) => (a.id === id ? { ...a, takenBy: '—' } : a)));
    } catch {
      /* left in the list, which is the safe direction: an order that looks
         unclaimed gets looked at twice, one that vanished gets made never. */
    } finally {
      setTakingId(null);
    }
  }, []);
  const [layout, setLayout] = useState<Layout>(
    () => (localStorage.getItem('luma.till.layout') as Layout) ?? 'search',
  );
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState(CATEGORY_ALL);
  const [lines, setLines] = useState<CartLine[]>([]);
  const [basketDiscountRial, setBasketDiscount] = useState(0);
  const [customerPhone, setCustomerPhone] = useState<string | null>(null);
  const [phoneDraft, setPhoneDraft] = useState('');
  const [modal, setModal] = useState<Modal>(null);
  const [notFoundCode, setNotFoundCode] = useState<string | null>(null);
  const [oversoldAdd, setOversoldAdd] = useState<string | null>(null);

  /*
   * Weighing mode.
   *
   * Armed by the «وزن» button and disarmed as soon as it is used: a mode that
   * stays on is a mode that asks for a weight on the next tin of tomatoes,
   * which is worse than not having it.
   */
  const [weighing, setWeighing] = useState(false);
  const [weighItem, setWeighItem] = useState<CatalogueItem | null>(null);
  const [weightDraft, setWeightDraft] = useState('');

  const [method, setMethod] = useState<PaymentMethod | 'split'>('cash');
  const [cash, setCash] = useState(0);
  const [cardState, setCardState] = useState<CardState>('idle');
  // What the reader last said, when it is the reader talking rather than the
  // cashier confirming by hand.
  const [cardOutcome, setCardOutcome] = useState<terminal.ChargeResult | null>(null);
  const [cardBlocked, setCardBlocked] = useState(false);

  /*
   * Kitchen tickets the pass never got on paper.
   *
   * The agent retries a failed print by itself, so this is not what makes
   * printing work. It is here because a ticket that keeps failing looks on the
   * آشپزخانه screen exactly like one that printed: the kitchen is not making
   * the food and nobody at the counter knows, and a table waits for an order
   * that was never cooked.
   *
   * Dismissed tickets are remembered so the modal does not reappear every
   * twenty seconds at somebody who has already seen it and is dealing with
   * the printer. A ticket that fails AFTER being dismissed is a new fact and
   * shows again.
   */
  const unprinted = useQuery({
    queryKey: ['unprinted-kitchen-tickets'],
    queryFn: () => api.get<{ tickets: { ticketId: string; number: string; error: string }[] }>(
      '/kitchen/unprinted',
    ),
    refetchInterval: 20_000,
  });
  const [printDismissed, setPrintDismissed] = useState<string[]>([]);
  const [retryingTicket, setRetryingTicket] = useState<string | null>(null);

  const unprintedShowing = (unprinted.data?.tickets ?? [])
    .filter((t) => !printDismissed.includes(t.ticketId));

  async function retryTicketPrint(ticketId: string) {
    setRetryingTicket(ticketId);
    try {
      await api.post(`/kitchen/tickets/${encodeURIComponent(ticketId)}/print`, {});
    } catch {
      /*
       * Swallowed deliberately: the refetch below tells the truth either way.
       * If it still will not print the ticket stays on the list and the modal
       * stays up, which is the honest outcome and needs no second message.
       */
    } finally {
      setRetryingTicket(null);
      void unprinted.refetch();
    }
  }

  /*
   * Apply a whole card state at once.
   *
   * Three paths open the payment screen and each used to set these fields by
   * hand; one of them forgot the lock, and a single unknown result then
   * stranded every later basket. Setting them as a unit is what stops a
   * fourth path doing the same.
   */
  /*
   * Payments the guard is holding, and the one control that can release them.
   *
   * The lock on an ambiguous payment was complete except for this: the screen
   * told a cashier to fetch a سرشیفت and then gave the سرشیفت nothing to press,
   * so a held payment blocked its basket until somebody called the API by
   * hand. One sat for a day and blocked a counter mid-service.
   *
   * Polled rather than pushed. A held payment is rare and never urgent to the
   * second; thirty seconds is well inside the time it takes to fetch someone.
   */
  const held = useQuery({
    queryKey: ['held-card-payments'],
    queryFn: () => terminal.pending(),
    refetchInterval: 30_000,
  });
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  async function resolveHeld(
    invoiceId: string,
    resolution: 'charged' | 'not_charged',
    by: string,
  ) {
    setResolving(true);
    setResolveError(null);
    try {
      await terminal.resolve(invoiceId, resolution, by);
      await held.refetch();
      /*
       * The basket this released may be the one on screen, so lift the local
       * lock too — through the same freshCard the other three paths use, so
       * this cannot become a fourth spelling of what a clear basket means.
       */
      applyCard(freshCard());
      setPayError(null);
    } catch (e) {
      setResolveError(
        e instanceof Error ? e.message : 'ثبت وضعیت ممکن نشد — دوباره تلاش کنید',
      );
    } finally {
      setResolving(false);
    }
  }

  function applyCard(c: CardFields) {
    setCardState(c.state);
    setCardOutcome(c.outcome);
    setCardBlocked(c.blocked);
  }
  const [rrn, setRrn] = useState('');
  const [splitCashRial, setSplitCash] = useState(0);
  const [payError, setPayError] = useState<string | null>(null);
  const [sale, setSale] = useState<SaleResult | null>(null);
  const [soldLines, setSoldLines] = useState<CartLine[]>([]);
  /*
   * The table being paid for, when the payment screen is settling one rather
   * than selling a basket.
   *
   * There is one payment screen in this app and it is the only place money is
   * taken. A table used to be settled by two buttons on the tables screen that
   * posted a method name — so «کارتخوان» there recorded a card payment no reader
   * had been asked for. Everything the payment screen knows how to do (drive the
   * terminal, refuse to confirm until it answers, hold an ambiguous outcome,
   * split, capture an RRN) simply did not apply to a table's bill.
   */
  const [settling, setSettling] = useState<SettleTarget | null>(null);

  const [lastSale, setLastSale] = useState<{ number: string; totalRial: number } | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [parked, setParked] = useState<ParkedSale[]>([]);
  const [parkedCarts, setParkedCarts] = useState<Record<string, { lines: CartLine[]; discount: number; phone: string | null }>>({});

  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);

  const catalogue = useQuery({
    queryKey: ['till-catalogue', session.branchId],
    queryFn: () => api.get<Catalogue>(`/till/catalogue?branchId=${encodeURIComponent(session.branchId)}`),
    /*
     * Polled, because nothing else here ever fires.
     *
     * react-query's other refresh triggers are focus, mount and reconnect. A
     * till is none of those things: it opens fullscreen at the start of a
     * shift and holds focus until the shop closes, so the window never blurs,
     * the component never remounts and the connection never drops. With only
     * a staleTime the catalogue was effectively frozen at sign-in — a product
     * added in the panel did not appear until someone restarted the app, and
     * the only person who could know that was whoever added it.
     *
     * A minute is well inside the time it takes to walk back to the counter,
     * and one small GET per till per minute is nothing next to the sales
     * traffic it sits beside. It still does not refetch per keystroke, which
     * is what the original note was protecting.
     *
     * Safe during a sale: a cart line holds its own CatalogueItem, so a
     * refetch replaces what is on the shelf, never what is already rung up.
     */
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  const items = catalogue.data?.items ?? [];
  // The shop's arrangement travels with the catalogue; the till renders it
  // rather than working it out again from the items.
  /*
   * The chips, keyed by identity where the device has one.
   *
   * categoryList arrives from an agent that folds category.upserted; a server
   * that does not send it, or a build that never folded the type, leaves only
   * the flat names. Both are normalised to the same shape here so the screen
   * has one thing to render, and where there is no id the name IS the id —
   * which is exactly the behaviour this replaced.
   *
   * Only shelves something sits on. The payload's categoryList deliberately
   * includes empty ones, because a shop's arrangement is not derivable from its
   * stock; but at a counter a chip that filters to nothing is a dead tap, and
   * the flat `categories` list is already "the shelves these items are on".
   */
  const shelves = useMemo<Shelf[]>(
    () => (catalogue.data ? shelvesFrom(catalogue.data) : []),
    [catalogue.data],
  );
  const basketTotals = useMemo(
    () => totalsOf(lines, basketDiscountRial),
    [lines, basketDiscountRial],
  );
  /*
   * What is owed, whichever thing is being paid for.
   *
   * A table's bill is not recomputed here. It was priced when its rounds were
   * fired — a price change at nine must not reprice what a table ate at seven —
   * so this carries the agent's figure through for display and for the cash and
   * split arithmetic. The agent recomputes it from the rounds on settle and
   * refuses one that pays less, so a stale number here fails loudly instead of
   * under-ringing the table.
   */
  const totals = useMemo(
    () => (settling
      ? { subtotalRial: settling.totalRial, lineDiscountRial: 0, basketDiscountRial: 0,
          vatRial: 0, totalRial: settling.totalRial, count: settling.lines.length }
      : basketTotals),
    [settling, basketTotals],
  );

  const canDiscount = session.permissions.includes('sell.discount');

  /* --- basket ------------------------------------------------------------- */

  /*
   * The basket, mirrored for addItem.
   *
   * addItem must know the quantity this add will RESULT in — three of a thing
   * with two on the shelf is an oversell, and the third tap is where that
   * becomes true. Reading it from state would mean depending on `lines`, which
   * changes on every keystroke; the functional update below still owns
   * correctness either way.
   */
  const linesRef = useRef<CartLine[]>([]);
  useEffect(() => { linesRef.current = lines; }, [lines]);

  /*
   * Cleared on a timer, not by a button.
   *
   * It reports something that already happened and needs no decision, so
   * making the cashier dismiss it adds a tap to every oversell. Long enough to
   * read twice; short enough that it is never describing an earlier basket.
   */
  useEffect(() => {
    if (oversoldAdd === null) return;
    const t = setTimeout(() => setOversoldAdd(null), 7000);
    return () => clearTimeout(t);
  }, [oversoldAdd]);

  const addItem = useCallback((item: CatalogueItem, quantity = 1) => {
    /*
     * Said where the cashier is looking.
     *
     * The cart already warns, and the cart is on the other side of the screen
     * from the scanner. Someone scanning a barcode is watching the scan field:
     * the row they just added is gone, the «تمام شد» chip went with it, and
     * the only sign is a banner off to the side. So the add says it too, next
     * to the search — the same place a bad barcode reports itself.
     *
     * A notice, never a prompt. A modal at a counter with a queue behind it is
     * its own failure, and the till does not refuse this sale.
     */
    const held = linesRef.current.find((l) => l.item.variantId === item.variantId);
    const resulting = Number(((held?.quantity ?? 0) + quantity).toFixed(3));
    if (soldPastZero(item, resulting)) {
      setOversoldAdd(item.name);
    }

    setLines((ls) => {
      const i = ls.findIndex((l) => l.item.variantId === item.variantId);
      if (i > -1) {
        const next = ls.slice();
        next[i] = { ...next[i], quantity: Number((next[i].quantity + quantity).toFixed(3)) };
        return next;
      }
      return [...ls, { item, quantity, discountRial: 0 }];
    });
  }, []);

  /*
   * A scan that matches exactly one barcode is added straight to the basket.
   *
   * This is the difference between a till that feels instant and one that makes
   * the cashier confirm every item. The scanner types the code and presses
   * Enter; if exactly one product carries it there is nothing to choose.
   * Anything ambiguous falls through to the results list, and a code nothing
   * matches surfaces the not-found banner rather than silently doing nothing.
   */
  useEffect(() => {
    const onEnter = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || screen !== 'sale') return;
      const code = toLatin(query.trim());
      if (code === '') return;

      const exact = items.filter((i) => toLatin(i.barcode ?? '') === code || toLatin(i.sku ?? '') === code);
      if (exact.length === 1) {
        e.preventDefault();
        addItem(exact[0]);
        setQuery('');
        setNotFoundCode(null);
        return;
      }
      // Only a code-shaped string can be "not found" — a half-typed product
      // name is not a failed scan and must not raise an alarm.
      if (exact.length === 0 && /^\d{6,}$/.test(code)) {
        const anyMatch = items.some((i) => toLatin(i.name).includes(code));
        if (!anyMatch) { e.preventDefault(); setNotFoundCode(code); }
      }
    };
    window.addEventListener('keydown', onEnter);
    return () => window.removeEventListener('keydown', onEnter);
  }, [query, items, screen, addItem]);

  function bump(variantId: string, delta: number) {
    setLines((ls) =>
      ls.map((l) =>
        l.item.variantId === variantId
          ? { ...l, quantity: Math.max(1, Number((l.quantity + delta).toFixed(3))) }
          : l,
      ),
    );
  }

  function resetSale() {
    setLines([]);
    setBasketDiscount(0);
    setCustomerPhone(null);
    setQuery('');
    setCash(0);
    setRrn('');
    setSplitCash(0);
    applyCard(freshCard());
    setMethod('cash');
    setPayError(null);
    setNotFoundCode(null);
    // A finished settle must not leave the payment screen pointed at a table
    // that has been closed — the next sale would post to its settle endpoint.
    setSettling(null);
  }

  /* --- park --------------------------------------------------------------- */

  function park() {
    if (lines.length === 0) return;
    const id = `k${Date.now()}`;
    const now = new Date();
    setParked((p) => [
      ...p,
      {
        id, who: customerPhone ?? 'مهمان', lineCount: lines.length,
        totalRial: totals.totalRial,
        parkedAt: `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
      },
    ]);
    setParkedCarts((c) => ({ ...c, [id]: { lines, discount: basketDiscountRial, phone: customerPhone } }));
    resetSale();
  }

  function retrieve(id: string) {
    const cart = parkedCarts[id];
    if (!cart) return;
    setLines(cart.lines);
    setBasketDiscount(cart.discount);
    setCustomerPhone(cart.phone);
    setParked((p) => p.filter((k) => k.id !== id));
    setParkedCarts((c) => { const next = { ...c }; delete next[id]; return next; });
    setModal(null);
  }

  /* --- payment ------------------------------------------------------------ */

  const paymentsFor = (reference: string): SalePayment[] =>
    composePayments(method, totals.totalRial, splitCashRial, reference);

  const submit = useMutation({
    /*
     * The terminal's reference is passed in, never read from state.
     *
     * setRrn() followed immediately by mutate() reads the PREVIOUS value —
     * React has not applied the update yet — so the recorded payment went out
     * with no reference at all. That is the number which reconciles the sale
     * against the bank's settlement report and the one a shopkeeper quotes in
     * a dispute, and losing it is invisible until someone tries to match a
     * day's card takings.
     */
    mutationFn: (override?: { rrn?: string }) =>
      /*
       * A table's bill and a walk-in basket are the same act of taking money,
       * and they reach the agent through the endpoint that owns each. The
       * payments are built the same way for both: `payments` on a settle is the
       * itemised shape /sales posts, and the agent documents it as the way a
       * till settles a table through the counter's own payment screen.
       *
       * The clientRef is the check's, so a settle retried after a timeout finds
       * the first invoice instead of issuing the table a second one.
       */
      settling
      ? api.post<SaleResult>(settlePath(settling.checkId), {
        payments: paymentsFor(override?.rrn ?? rrn),
        clientRef: clientRef,
      })
      : api.post<SaleResult>('/sales', {
        branchId: session.branchId,
        shiftId: session.shiftId ?? '',
        /*
         * stockOverride marks the lines actually sold past zero, and only
         * those.
         *
         * The till never refuses a sale — the customer is holding the item, and
         * refusing over a number that is already wrong loses a real sale to a
         * counting error. So negative stock is legitimate here. But a negative
         * level is ambiguous on its own: it looks identical whether a cashier
         * knowingly sold the last one or the shelf has been drifting unnoticed
         * for a month, and those call for opposite responses — retrain, or
         * recount. This flag is what tells them apart; who did it is already on
         * the envelope.
         *
         * Per line, never per basket. Set on every line, an owner reviewing
         * overrides is just reading the day's takings.
         */
        lines: lines.map((l) => ({
          variantId: l.item.variantId,
          productId: l.item.productId,
          quantity: l.quantity,
          discountToman: Math.round(l.discountRial / 10),
          ...(soldPastZero(l.item, l.quantity) ? { stockOverride: true } : {}),
        })),
        payments: paymentsFor(override?.rrn ?? rrn),
        discountToman: Math.round(basketDiscountRial / 10),
        customerPhone: customerPhone ?? '',
        // Stable for this basket, so a retry after a timeout returns the
        // original sale rather than charging the customer twice.
        clientRef: clientRef,
      }),
    onSuccess: (res) => {
      setSale(res);
      /*
       * The paper, immediately, because the button promised it.
       *
       * «تأیید پرداخت نقدی و چاپ فاکتور» said "and print" for a long time with
       * nothing behind it: the sale recorded, the screen advanced, and no paper
       * came out. Our user found it by standing at a counter waiting.
       *
       * Fired and not awaited. The sale is already recorded and the customer is
       * owed their change now — making them wait on a printer socket to find
       * out whether it has paper is the wrong order of business. A failure
       * surfaces as a message on the receipt screen, where the reprint button
       * is.
       */
      void printReceipt(res.id, false);
      // A settle has no cart. The receipt shows the rounds the table actually
      // ate, priced as they were fired.
      setSoldLines(settling ? settleLinesAsCart(settling) : lines);
      setLastSale({ number: res.number, totalRial: res.totalRial });
      setScreen('receipt');
      // The catalogue's stock counts just moved.
      void qc.invalidateQueries({ queryKey: ['till-catalogue'] });
    },
    onError: (e: ServerError) => setPayError(e.detail ?? e.message),
  });

  /*
   * One client reference per basket, minted when payment is opened.
   *
   * It must survive a retry — that is the entire point — so it cannot be
   * regenerated on each attempt, and it must not survive into the next sale, or
   * the second customer's basket would be rejected as a duplicate of the first.
   */
  const [clientRef, setClientRef] = useState('');
  /*
   * True while the amount is at the reader.
   *
   * Not cardState. That is 'waiting' merely because the cashier picked the
   * card method, so it cannot answer "is a charge in flight" — and the send
   * button was disabled on `submitting`, the SALE mutation's flag, which is
   * false for the whole time the customer is entering their PIN. The button
   * sat live and enabled through exactly the window its own comment warned
   * about.
   */
  const [charging, setCharging] = useState(false);
  // The agent asking whether a repeated basket is a second customer.
  const [dupAsk, setDupAsk] = useState<string | null>(null);

  /*
   * Send the amount to the card reader, and act on what it says.
   *
   * The basket's clientRef is the identity the guard blocks on, because the
   * thing that must not be paid twice is the basket — and it exists before any
   * invoice does.
   *
   * The three outcomes are deliberately not two. Approved records the sale
   * carrying the reader's reference. Declined leaves the basket untouched so
   * the cashier can retry or take cash. Unknown records nothing at all and
   * locks the basket: the customer may already have paid, and the only safe
   * next step is a person reading the terminal's own receipt.
   */
  async function sendToTerminal(confirmDuplicate = false) {
    /*
     * Refused here as well as disabled in the UI.
     *
     * A disabled button is a rendering, and a rendering is one repaint behind
     * a fast second tap on a touchscreen. The state is the authority.
     */
    if (charging) return;

    setPayError(null);
    setCardState('waiting');
    setCardOutcome(null);
    setCharging(true);

    let res: terminal.ChargeResult;
    try {
      res = await terminal.charge(
        clientRef,
        Math.round(totals.totalRial / 10),
        basketFingerprint(
          lines.map((l) => ({
            variantId: l.item.variantId,
            quantity: l.quantity,
            discountRial: l.discountRial,
          })),
          totals.totalRial,
          basketDiscountRial,
        ),
        confirmDuplicate,
      );
    } finally {
      // Released whatever happened: a charge that threw must not leave the
      // button dead for the rest of the shift.
      setCharging(false);
    }
    setCardOutcome(res);

    /*
     * The agent has seen this basket charged a moment ago and wants an answer
     * before the money moves. Nothing is in doubt and nothing is locked — the
     * amount never reached the reader.
     */
    if (res.needsConfirmation) {
      setCardState('idle');
      setCardOutcome(null);
      setDupAsk(res.message);
      return;
    }

    switch (res.outcome) {
      case 'approved':
        setRrn(res.rrn ?? '');
        setCardState('idle');
        // Handed straight to the mutation: the state above is for the receipt,
        // and will not have been applied by the time this runs.
        submit.mutate({ rrn: res.rrn ?? '' });
        return;
      case 'declined':
        setCardState('declined');
        setPayError(res.message);
        return;
      case 'unavailable':
        // No reader on this till. Fall back to the manual flow, which is what
        // a shop with a standalone کارتخوان does anyway.
        setCardState('waiting');
        return;
      default:
        // Unknown. Nothing is recorded and the basket is locked.
        setCardState('waiting');
        setCardBlocked(true);
        setPayError(res.message);
        return;
    }
  }

  function openPayment() {
    if (lines.length === 0) return;
    /*
     * The basket's identity, and the key the double-charge guard blocks on.
     *
     * Falls back to the device when no cashier has signed in — an agent-mode
     * till has no user yet, and a ref beginning with "-" is both ugly on a
     * support call and not actually unique between two such devices.
     */
    setClientRef(`${session.userId || session.deviceCode || 'till'}-${Date.now()}`);
    setPayError(null);
    setCash(0);
    setCardState('idle');
    /*
     * A new basket, so a new card state. freshCard is the one definition of
     * what that means; see cardlock.ts for why it is not spelled out here.
     */
    applyCard(freshCard());
    setSplitCash(Math.round(totals.totalRial / 2 / 10) * 10);
    setScreen('payment');
  }

  /*
   * Open the payment screen on a table's bill.
   *
   * The clientRef is the check's own — «chk:<id>» — and it is what the
   * double-charge guard blocks on, so a reader asked twice for the same table
   * is recognised as the same charge rather than a second one.
   */
  function payTable(target: SettleTarget) {
    setSettling(target);
    setClientRef(checkRef(target.checkId));
    setMethod('cash');
    setPayError(null);
    setCash(0);
    setRrn('');
    applyCard(freshCard());
    setSplitCash(Math.round(target.totalRial / 2 / 10) * 10);
    setScreen('payment');
  }

  /*
   * Printing is separate from recording, and its failure is not the sale's.
   *
   * A printer that is out of paper must not turn a completed sale into an
   * error on screen — the money is taken and the invoice exists. So this
   * reports rather than throws, and the receipt screen offers another go.
   */
  const [printState, setPrintState] = useState<'idle' | 'printing' | 'done' | 'failed'>('idle');
  const [printError, setPrintError] = useState<string | null>(null);

  const printReceipt = useCallback(async (invoiceID: string, reprint: boolean) => {
    setPrintState('printing');
    setPrintError(null);
    try {
      await api.post(`/invoices/${encodeURIComponent(invoiceID)}/print`, { reprint });
      setPrintState('done');
    } catch (e) {
      setPrintState('failed');
      setPrintError((e as Error)?.message ?? 'چاپ فاکتور انجام نشد');
    }
  }, []);

  const net: NetState = !online ? 'offline' : 'online';

  /*
   * The agent's own view of its sync, which the browser's navigator.onLine
   * cannot give us.
   *
   * `online` above is whether this BOARD has a network. It says nothing about
   * whether the cloud is accepting what the till sends — and those came apart
   * badly: a till sat on a healthy connection for a day with every event it
   * wrote permanently refused, and the header said آنلاین — همه‌چیز همگام the
   * whole time because nothing on the screen read anything but the browser.
   *
   * Thirty seconds: a refused event is not a transient condition that clears
   * itself, so there is nothing to catch quickly, and the count only ever needs
   * to arrive before somebody closes up.
   */
  const agentStatus = useQuery({
    queryKey: ['till-sync-status'],
    /*
     * version and build are the agent's own stamps. build is the compiled-in
     * `git describe` and cannot disagree with the running binary; version is
     * whatever the wrapper supplied, which on Android is the wrapper's number
     * and not the agent's. So build is what gets shown, and version is read
     * only to stand in when a build predates the stamp.
     */
    queryFn: () =>
      api.get<{
        pending?: number;
        refused?: number;
        deviceId?: string;
        version?: string;
        build?: string;
      }>('/status'),
    refetchInterval: 30_000,
  });

  return (
    <Shell
      shopName={session.tenantName}
      branchName={session.branchName}
      cashierName={session.name}
      cashierInitials={initialsOf(session.name)}
      shiftSince={session.shiftSince}
      net={net}
      queued={agentStatus.data?.pending ?? 0}
      refused={agentStatus.data?.refused ?? 0}
      agentBuild={agentStatus.data?.build || agentStatus.data?.version}
      menu={
        /*
         * The way out of the sale screen.
         *
         * Deliberately not a nav bar down the side: every pixel of this screen
         * is the hot path, and the four things below are used a handful of
         * times a shift each. A menu keeps them one tap away without taking
         * room from the basket.
         */
        <TillMenu
          onZ={() => setScreen('z')}
          onReturns={() => setScreen('returns')}
          onTools={() => setScreen('tools')}
          onArrivals={() => setScreen('arrivals')}
          onTables={() => setScreen('tables')}
          arrivalCount={arrivals.filter((a) => !a.takenBy).length}
          openTables={openTables}
        />
      }
    >
      {screen === 'tables' && (
        <TablesScreen onBack={() => setScreen('sale')} onSettle={payTable} />
      )}

      {screen === 'arrivals' && (
        <ArrivalsScreen
          arrivals={arrivals}
          staleUnclaimed={staleArrivals}
          onTake={takeArrival}
          onFire={fireArrival}
          onOpen={(a) => { setArrivalError(null); setOpenArrival(a); }}
          onBack={() => setScreen('sale')}
          busyId={takingId}
        />
      )}

      {screen === 'sale' && (
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <SaleScreen
            catalogue={items}
            shelves={shelves}
            loading={catalogue.isLoading}
            query={query}
            onQuery={(v) => { setQuery(v); if (notFoundCode) setNotFoundCode(null); }}
            category={category}
            onCategory={setCategory}
            layout={layout}
            onAdd={(item) => {
              if (weighing) {
                setWeighItem(item);
                setWeightDraft('');
                setModal('quantity');
                setWeighing(false);
                return;
              }
              addItem(item);
            }}
            notFoundCode={notFoundCode}
            onDismissNotFound={() => setNotFoundCode(null)}
            oversoldAdd={oversoldAdd}
            weighing={weighing}
            onWeigh={() => setWeighing((w) => !w)}
            onLayout={(next) => { setLayout(next); localStorage.setItem('luma.till.layout', next); }}
            offline={!online}
            queued={agentStatus.data?.pending ?? 0}
          />
          <CartPanel
            lines={lines}
            totals={totals}
            invoiceLabel="فاکتور جدید"
            parkedCount={parked.length}
            customerName={customerPhone}
            customerMeta={customerPhone ? 'فاکتور الکترونیکی برای این شماره ارسال می‌شود' : null}
            onInc={(id) => bump(id, 1)}
            onDec={(id) => bump(id, -1)}
            onRemove={(id) => setLines((ls) => ls.filter((l) => l.item.variantId !== id))}
            onPay={openPayment}
            onDiscount={() => setModal('discount')}
            onPark={park}
            onOpenParked={() => setModal('park')}
            onClear={resetSale}
            onCustomer={() => { setPhoneDraft(''); setModal('customer'); }}
            onDropCustomer={() => setCustomerPhone(null)}
            paying={submit.isPending}
          />
        </div>
      )}

      {screen === 'payment' && (
        <PaymentScreen
          totals={totals}
          invoiceLabel={settling
            ? `${settling.tableLabel || settling.number} — تسویه`
            : 'فاکتور جدید'}
          method={method}
          onMethod={(m) => {
            setMethod(m);
            setPayError(null);
            setCardOutcome(null);
            setCardState(m === 'card' ? 'waiting' : 'idle');
          }}
          cash={cash}
          onCash={setCash}
          onKey={(k) => {
            setCash((c) => {
              let v = String(Math.round(c));
              if (k === 'del') v = v.length > 1 ? v.slice(0, -1) : '0';
              else if (k === '000') v = v === '0' ? '0' : v + '000';
              else v = v === '0' ? k : v + k;
              return Math.min(999_999_999, parseInt(v, 10) || 0);
            });
          }}
          cardState={cardState}
          cardOutcome={cardOutcome}
          cardBlocked={cardBlocked}
          resolvePanel={<ResolveHeldPayment
              attempts={held.data ?? []}
              onResolve={resolveHeld}
              error={resolveError}
              busy={resolving}
            />}
          onSendToTerminal={() => { void sendToTerminal(); }}
          onCardApproved={() => submit.mutate(undefined)}
          onCardDeclinedRetry={() => setCardState('declined')}
          rrn={rrn}
          onRrn={setRrn}
          splitCashRial={splitCashRial}
          onSplitCash={setSplitCash}
          onConfirm={() => submit.mutate(undefined)}
          onBack={() => { setSettling(null); setScreen(settling ? 'tables' : 'sale'); }}
          submitting={submit.isPending}
          charging={charging}
          error={payError}
          offline={!online}
        />
      )}

      {screen === 'receipt' && sale && (
        <ReceiptScreen
          sale={sale}
          lines={soldLines}
          shopName={session.tenantName}
          branchName={session.branchName}
          cashierName={session.name}
          economicCode={session.economicCode}
          customerPhone={customerPhone}
          paidMethodLabel={METHOD_LABEL[method] ?? method}
          paidRial={method === 'cash' ? Math.max(cash * 10, sale.totalRial) : sale.totalRial}
          changeRial={method === 'cash' ? Math.max(0, cash * 10 - sale.totalRial) : 0}
          queued={!online}
          onNewSale={() => { resetSale(); setSale(null); setPrintState('idle'); setPrintError(null); setScreen('sale'); }}
          printState={printState}
          printError={printError}
          onPrint={() => { void printReceipt(sale.id, printState === 'done'); }}
        />
      )}

      {screen === 'z' && session.shiftId && (
        <ZScreen
          shiftId={session.shiftId}
          canClose={session.permissions.includes('sell.shift_close')}
          onClosed={() => { setToast('شیفت بسته شد'); onShiftClosed(); }}
          onBack={() => setScreen('sale')}
        />
      )}

      {screen === 'returns' && (
        <ReturnsScreen
          canRefund={session.permissions.includes('sell.refund')}
          onDone={(m) => { setToast(m); setScreen('sale'); void qc.invalidateQueries({ queryKey: ['till-catalogue'] }); }}
          onBack={() => setScreen('sale')}
        />
      )}

      {screen === 'tools' && (
        <ToolsScreen
          catalogue={items}
          lastSale={lastSale}
          canOpenDrawer={session.permissions.includes('sell.drawer')}
          agent={agentStatus.data}
          onBack={() => setScreen('sale')}
          resolvePanel={<ResolveHeldPayment
              attempts={held.data ?? []}
              onResolve={resolveHeld}
              error={resolveError}
              busy={resolving}
            />}
        />
      )}

      {openArrival && (
        <ArrivalSheet
          arrival={openArrival}
          onClose={() => setOpenArrival(null)}
          onFire={(id) => { void fireArrival(id); setOpenArrival(null); }}
          onTake={(id) => { void takeArrival(id); setOpenArrival(null); }}
          onReprint={(t) => { void reprintTicket(t); }}
          onPay={(c) => { void payArrival(c); }}
          busy={takingId !== null}
          error={arrivalError}
        />
      )}

      {/*
        * The duplicate-basket question.
        *
        * Above the arrivals overlay deliberately: this one is holding a payment
        * a cashier is standing there waiting to take, and it is the only overlay
        * whose answer costs money either way.
        */}
      {dupAsk !== null && (
        <ChargeAgainModal
          message={dupAsk}
          onCancel={() => setDupAsk(null)}
          onConfirm={() => { setDupAsk(null); void sendToTerminal(true); }}
        />
      )}

      {toast && <Toast message={toast} onDone={() => setToast(null)} />}

      {/*
        * Held back while a payment is on screen.
        *
        * Everywhere else an overlay costs a glance; on the payment screen it
        * lands between a cashier and a card machine, and the order it is
        * announcing is not more urgent than the one being paid for. The queue
        * survives, so it appears the moment they are done.
        */}
      {unprintedShowing.length > 0 && (
        <TicketDidNotPrintModal
          tickets={unprintedShowing}
          onRetry={retryTicketPrint}
          busyId={retryingTicket}
          onClose={() => setPrintDismissed((d) => [
            ...d, ...unprintedShowing.map((t) => t.ticketId),
          ])}
        />
      )}

      {unannounced.length > 0 && screen !== 'payment' && (
        <NewArrivalModal
          /*
           * paid is stated by the agent now; kind is the fallback for one too
           * old to send it. Never left undefined — the modal renders «پرداخت‌نشده»
           * for anything falsy, and saying that about an order the customer has
           * already paid for invites charging them twice.
           */
          arrivals={unannounced.map((a) => ({ ...a, paid: a.paid ?? a.kind === 'kiosk' }))}
          onOpenList={() => { setUnannounced([]); setScreen('arrivals'); }}
          onClose={() => setUnannounced([])}
        />
      )}

      {modal === 'park' && (
        <ParkModal parked={parked} onRetrieve={retrieve} onClose={() => setModal(null)} />
      )}
      {modal === 'customer' && (
        <CustomerModal
          draft={phoneDraft}
          onDraft={setPhoneDraft}
          onAttach={() => { setCustomerPhone(phoneDraft); setModal(null); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal === 'quantity' && weighItem && (
        <QuantityModal
          itemName={weighItem.name}
          unitPriceRial={weighItem.priceRial}
          draft={weightDraft}
          onDraft={setWeightDraft}
          onConfirm={() => {
            const v = Number(weightDraft.replace('٫', '.')) || 0;
            if (v > 0) addItem(weighItem, v);
            setModal(null);
            setWeighItem(null);
          }}
          onClose={() => { setModal(null); setWeighItem(null); }}
        />
      )}
      {modal === 'discount' && (
        <DiscountModal
          subtotalRial={totals.subtotalRial}
          currentRial={basketDiscountRial}
          canApprove={canDiscount}
          onPick={(r) => { setBasketDiscount(r); setModal(null); }}
          onClear={() => { setBasketDiscount(0); setModal(null); }}
          onClose={() => setModal(null)}
        />
      )}
    </Shell>
  );
}

/**
 * The menu behind the header.
 *
 * Closed by clicking anywhere else, because a menu that only closes via its own
 * button is a menu a cashier leaves open over the basket.
 */
function TillMenu({ onZ, onReturns, onTools, onArrivals, onTables, arrivalCount, openTables }: {
  onZ: () => void; onReturns: () => void; onTools: () => void;
  onArrivals: () => void; onTables: () => void;
  arrivalCount: number; openTables: number;
}) {
  const [open, setOpen] = useState(false);
  const [confirmExit, setConfirmExit] = useState(false);

  // A null action is an item that names something this deployment cannot do.
  const items: [string, (() => void) | null][] = [
    /*
     * First, and carrying its own count.
     *
     * Everything else in this menu is something a cashier goes looking for. An
     * arriving order is the one thing that needs finding them — a customer has
     * already paid and is waiting, so it cannot sit behind a menu that gives no
     * sign anything is in it.
     */
    [arrivalCount > 0 ? `سفارش‌های ورودی (${fa(arrivalCount)})` : 'سفارش‌های ورودی', onArrivals],
    /*
     * Above مرجوعی because it is money waiting to be taken, not an exception
     * being handled. The count is in the label for the same reason the
     * arrivals count is: a table nobody settles is a bill nobody pays.
     */
    [openTables > 0 ? `میزهای باز (${fa(openTables)})` : 'میزهای باز', onTables],
    /*
     * Returns are cloud-only, and the menu says so rather than opening a screen
     * that cannot answer.
     *
     * The agent serves the sale path from its own store, but it has no
     * /till/invoices and no /invoices/{id}/refund — there is nowhere local to
     * look a receipt up. Left enabled, «مرجوعی» opens, the cashier types a
     * receipt number in front of a customer holding the goods, and gets an
     * error that names none of this.
     */
    ...(target() === 'agent'
      ? ([['مرجوعی — فعلاً فقط آنلاین', null]] as [string, null][])
      : ([['مرجوعی', onReturns]] as [string, () => void][])),
    ['ابزار سریع', onTools],
    ['بستن شیفت — گزارش Z', onZ],
  ];

  /*
   * Only inside the till window.
   *
   * The same bundle runs in a browser for demos, where there is no window to
   * close and chrome.webview does not exist. A dead «خروج» there would be a
   * button that does nothing, which this product does not ship.
   */
  if (hostedInTill()) {
    items.push(['خروج از برنامه', () => setConfirmExit(true)]);
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="منوی صندوق"
        style={{
          width: 44, height: 44, borderRadius: 10, fontSize: 18,
          border: '1px solid var(--luma-line-strong)', background: 'var(--luma-card)',
        }}
      >
        ☰
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 20 }} onClick={() => setOpen(false)} />
          <div
            style={{
              position: 'absolute', insetInlineEnd: 0, top: 50, zIndex: 21, width: 240,
              background: 'var(--luma-card)', border: '1px solid var(--luma-line)',
              borderRadius: 12, boxShadow: '0 12px 32px rgba(32,30,29,.22)', overflow: 'hidden',
            }}
          >
            {items.map(([label, go]) => (
              <button
                key={label}
                disabled={go === null}
                onClick={() => { if (!go) return; setOpen(false); go(); }}
                style={{
                  width: '100%', minHeight: 52, padding: '0 16px', textAlign: 'right',
                  fontSize: 14, fontWeight: 600, borderBottom: '1px solid var(--pos-rule-faint)',
                  cursor: go === null ? 'default' : 'pointer',
                  color: go === null
                    ? 'var(--pos-disabled-ink)'
                    : label === 'خروج از برنامه' ? 'var(--luma-tint-danger-text)' : undefined,
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </>
      )}

      {/*
        * Asked, not just done.
        *
        * Closing the till is the one item here that ends the session rather
        * than changing the screen, and it sits directly under «بستن شیفت» on a
        * touch screen. A mis-tap that shuts the shop's till mid-queue is worth
        * one confirmation.
        */}
      {confirmExit && (
        <ConfirmExit onCancel={() => setConfirmExit(false)} onConfirm={exitTill} />
      )}
    </div>
  );
}

function Toast({ message, onDone }: { message: string; onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 4000);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div
      style={{
        position: 'absolute', insetInline: 0, bottom: 24, zIndex: 30,
        display: 'flex', justifyContent: 'center', pointerEvents: 'none',
      }}
    >
      <div
        style={{
          maxWidth: 640, background: 'var(--pos-dark)', color: '#FFF',
          borderRadius: 12, padding: '12px 18px', fontSize: 14, fontWeight: 600,
          boxShadow: '0 12px 32px rgba(32,30,29,.32)',
        }}
      >
        {message}
      </div>
    </div>
  );
}

export type { Session };
export { toman };
